// The analysis, in the header of every page while it is queued or running, and once its report is ready
// (domain/analysisBadge.ts). It asks again every few seconds while there is something under way, and stops.

import "./AnalysisBadge.css";
import { useEffect, useState } from "preact/hooks";
import { useStore } from "../../state/useStore.ts";
import { K } from "../../state/keys.ts";
import * as api from "../../services/api.ts";
import * as storage from "../../services/storage.ts";
import { BADGE_POLL_MS, analysisBadge } from "../../domain/analysisBadge.ts";
import type { AnalysisStatus } from "../../domain/firstAnalysis.ts";
import { t } from "../../i18n/common.ts";

export function AnalysisBadge() {
    const user = useStore(K.user.currentUser);
    const [status, setStatus] = useState<AnalysisStatus | null>(null);

    useEffect(() => {
        if (!user) return;
        let live = true;
        let timer: ReturnType<typeof setTimeout> | null = null;
        const ask = () => {
            void api.fetchDiagnosisStatus()
                .then((answer) => {
                    if (!live) return;
                    const read = answer as AnalysisStatus | null;
                    setStatus(read);
                    const job = read?.job;
                    if (job && (job.status === "queued" || job.status === "running")) timer = setTimeout(ask, BADGE_POLL_MS);
                })
                .catch(() => undefined);
        };
        ask();
        return () => {
            live = false;
            if (timer) clearTimeout(timer);
        };
    }, [user]);

    const badge = analysisBadge(status, storage.loadReportSeenJob());
    if (!badge) return null;
    return (
        <a class="analysis-badge" href="/analysis" data-state={badge.state}
           title={[badge.label, badge.detail].filter(Boolean).join(" · ") || t("analysisOpen")}>
            <span class="analysis-badge__dot" aria-hidden="true" />
            <span class="analysis-badge__label">{badge.label}</span>
            {badge.detail && <span class="analysis-badge__detail">{badge.detail}</span>}
            {badge.percent !== null && (
                <span class="analysis-badge__bar" aria-hidden="true">
                    <span style={`width:${Math.max(3, badge.percent)}%`} />
                </span>
            )}
        </a>
    );
}
